<script lang="ts">
  // Continuity > Conversations — the cadence view (who's overdue for a
  // conversation). Admin-only; enforced server-side (RequireAdmin on every
  // /api/admin/continuity/people route). Used to also host the full People
  // register + a same-page person-detail panel; that detail now lives at
  // #/directory/person/:id (PersonPage.svelte), so this is just the
  // cadence table — dot, name, group, last conversation, talk to them by.
  import { untrack } from 'svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { peopleApi } from '$lib/api/people';
  import { route } from '$lib/state/route.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import InfoTip from '$lib/components/ui/InfoTip.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import type { OKRColour, PersonListRow } from '$lib/types';

  // initialOverdueOnly is the Continuity Home "People overdue a
  // conversation" card's click-through (via continuityNav.svelte.ts) — a
  // one-shot quick filter, cleared locally once the admin dismisses it.
  const { initialOverdueOnly = false }: { initialOverdueOnly?: boolean } = $props();

  const TALK_TO_THEM_BY_TOOLTIP =
    'Every 3 months for the first year, every 6 months after that. Do not hold it in the same month as a pay decision — the answers stop being honest.';

  const LAMP_TONE: Record<OKRColour, 'bad' | 'warn' | 'good'> = { red: 'bad', amber: 'warn', green: 'good' };

  const query = createQuery(() => ({ queryKey: ['continuity-people'], queryFn: () => peopleApi.list() }));

  let overdueOnly = $state(untrack(() => initialOverdueOnly));

  const today = new Date().toISOString().slice(0, 10);
  function isOverdue(date: string | null): boolean {
    return date !== null && date < today;
  }

  const filteredRows = $derived((query.data || []).filter((r) => (overdueOnly ? isOverdue(r.talk_to_them_by) : true)));

  function openPerson(row: PersonListRow) {
    route.push({ tab: 'directory', sub: 'person', userId: row.id });
  }
</script>

<div class="tym-page">
  <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
    <div>
      <p class="tym-section-title">
        Conversations
        <InfoTip text={TALK_TO_THEM_BY_TOOLTIP} />
      </p>
      <p class="tym-section-sub">Who's overdue for a conversation.</p>
    </div>
    {#if overdueOnly}
      <span class="tym-badge tym-badge--info">
        Overdue a conversation
        <button type="button" class="ml-1 font-bold" onclick={() => (overdueOnly = false)} aria-label="Clear filter">×</button>
      </span>
    {/if}
  </div>

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load people.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      columns={[
        { key: 'dot', label: '', width: '32px', cell: dot },
        { key: 'name', label: 'Name', cell: name },
        { key: 'group', label: 'Group', cell: group },
        { key: 'last_conversation_date', label: 'Last conversation', cell: lastConversation },
        { key: 'talk_to_them_by', label: 'Talk to them by', cell: talkToThemBy }
      ]}
      rows={filteredRows}
      rowKey={(row) => row.id}
      onRowClick={openPerson}
      emptyTitle="No active people"
    />
  {/if}
</div>

{#snippet dot(row: PersonListRow)}
  <span title={row.status_reason}><StatusLamp tone={LAMP_TONE[row.colour]} label={row.status_reason} /></span>
{/snippet}
{#snippet name(row: PersonListRow)}
  <span class="font-medium text-navy">
    {row.name}
    {#if row.notice_status === 'serving_notice'}
      <span class="tym-badge tym-badge--alert">serving notice</span>
    {/if}
  </span>
{/snippet}
{#snippet group(row: PersonListRow)}
  <span class="muted">{row.group || '—'}</span>
{/snippet}
{#snippet lastConversation(row: PersonListRow)}
  <span class="muted">{row.last_conversation_date || 'Never'}</span>
{/snippet}
{#snippet talkToThemBy(row: PersonListRow)}
  <span class={isOverdue(row.talk_to_them_by) ? 'font-medium text-red-600' : 'muted'}>{row.talk_to_them_by || '—'}</span>
{/snippet}
